import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";

import { cn } from "@/lib/utils";

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      /*
       * Mobile:
       * - Full available width
       * - Horizontal scrolling
       * - Never wraps
       * - Prevents the tabs from increasing page width
       */
      "flex w-full max-w-full items-center justify-start gap-0 overflow-x-auto overflow-y-hidden whitespace-nowrap rounded-lg bg-muted p-1 text-muted-foreground",

      /*
       * Hide scrollbar while keeping scrolling/swiping enabled.
       */
      "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",

      /*
       * Desktop:
       * Restore the original inline-sized tab list.
       */
      "md:inline-flex md:w-auto md:overflow-visible",

      /*
       * Better touch behaviour on mobile.
       */
      "touch-pan-x",

      className,
    )}
    {...props}
  />
));

TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      /*
       * shrink-0 is important:
       * each tab keeps its natural width instead of
       * shrinking and causing text/layout problems.
       */
      "inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow",

      className,
    )}
    {...props}
  />
));

TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 min-w-0 max-w-full ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className,
    )}
    {...props}
  />
));

TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
